import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

import type { CartSuggestionCardsPreviewProduct } from './cart-suggestion-cards-block.config';

/** Serializable product row used in CB2 suggestion cards preview scenarios. */
export type SuggestionCardsPreviewProduct = CartSuggestionCardsPreviewProduct;

/**
 * Serializable preview VM for `SuggestionCardsBlockComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type SuggestionCardsBlockScenarioContent = {
  /** Loaded suggestions; an empty array means the block is hidden. */
  suggestions: readonly SuggestionCardsPreviewProduct[];
};
export const SUGGESTIONCARDSBLOCK_LEGACY_FIELD_ORDER = [
  "title",
  "description",
  "suggestions_set",
  "display_type",
  "is_suggestion_card_sticky",
  "background_color",
  { type: 'heading', value: "Product cards" },
  "product_name_color",
  "product_description_color",
  "product_price_color",
  "product_image_placeholder_color",
  "product_border_color",
  "product_background_color",
  {
    type: 'groupedFields',
    handle: 'product_hover_colors',
    extras: [
      {
        type: 'messageUtil',
        options: {
          type: 'info',
          message:
            'Customize the colors when hovering over a product card. Leave fields empty to use the default product colors',
        },
      },
    ],
    fields: ['backgroundColor', 'nameColor', 'descriptionColor', 'priceColor'],
  },
  { type: 'separator' },
  { type: 'heading', value: "Kiosk Colors" },
  "title_color",
  "text_color",
  "card_color",
  "button_background_color",
  "button_text_color",
  "secondary_color",
  "price_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  suggestionCardsBlockDescriptor: _generatedSuggestionCardsBlockDescriptor,
  suggestionCardsBlockBlocDefinition,
  suggestionCardsBlockDashboardLayout,
  suggestionCardsBlockConfig,
  suggestionCardsBlockResolveConfig,
  suggestionCardsBlockProperties,
  suggestionCardsBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Suggestion cards",
    componentType: 'SuggestionCardsBlockComponent',
    category: 'content',
    description: "Suggestion cards.",
    keywords: [],
    sharedFields: {},
  },
  () => ({
    title: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: 'pages.blocks.suggestion-cards.title.label',
      },
    }),
    description: propertyValue({
      type: 'textarea',
      value: '',
      options: {
        label: 'pages.blocks.suggestion-cards.description.label',
        help: 'pages.blocks.suggestion-cards.description.help',
      },
    }),
    suggestions_set: propertyValue({
      type: 'suggestions_set_picker',
      value: '',
      options: {
        label: 'pages.blocks.suggestion-cards.suggestions-set.label',
      },
    }),
    display_type: propertyValue({
      type: 'select',
      value: 'grid',
      options: {
        label: 'pages.blocks.suggestion-cards.display-type.label',
        help: 'pages.blocks.suggestion-cards.display-type.help',
        multiple: false,
        choices: [
          {
            value: 'grid',
            label: 'pages.blocks.suggestion-cards.display-type.options.grid',
          },
          {
            value: 'horizontal_highlight',
            label: 'pages.blocks.suggestion-cards.display-type.options.horizontal-highlight',
          },
          {
            value: 'kiosk_grid',
            label: 'pages.blocks.suggestion-cards.display-type.options.kiosk-grid',
          },
        ],
      },
    }),
    is_suggestion_card_sticky: propertyValue({
      type: 'checkbox',
      value: false,
      options: {
        label: 'pages.blocks.suggestion-cards.is-suggestion-card-sticky.label',
        help: 'pages.blocks.suggestion-cards.is-suggestion-card-sticky.help',
      },
    }),
    background_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: 'transparent' },
      options: {
        label: 'pages.blocks.suggestion-cards.background-color.label',
      },
    }),
    title_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: '#000' },
      options: {
        label: 'pages.blocks.suggestion-cards.title-color.label',
      },
    }),
    text_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: '#000' },
      options: {
        label: 'pages.blocks.suggestion-cards.text-color.label',
        help: 'pages.blocks.suggestion-cards.text-color.help',
      },
    }),
    card_color: propertyValue({
      type: 'color',
      value: { type: 'default', value: '' },
      options: {
        label: 'pages.blocks.suggestion-cards.card-color.label',
        colorInheritFrom: 'product_background_color',
      },
    }),
    button_background_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-600' },
      options: {
        label: 'pages.blocks.suggestion-cards.button-background-color.label',
      },
    }),
    button_text_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-50' },
      options: {
        label: 'pages.blocks.suggestion-cards.button-text-color.label',
      },
    }),
    secondary_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-200' },
      options: {
        label: 'pages.blocks.suggestion-cards.secondary-color.label',
        help: 'pages.blocks.suggestion-cards.secondary-color.help',
      },
    }),
    price_color: propertyValue({
      type: 'color',
      value: { type: 'default', value: '' },
      options: {
        label: 'pages.blocks.suggestion-cards.price-color.label',
        help: 'pages.blocks.suggestion-cards.price-color.help',
        colorInheritFrom: 'product_price_color',
      },
    }),
    product_name_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-900' },
      options: {
        label: 'pages.blocks.suggestion-cards.product-name-color.label',
      },
    }),
    product_description_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-900' },
      options: {
        label: 'pages.blocks.suggestion-cards.product-description-color.label',
        help: 'pages.blocks.suggestion-cards.product-description-color.help',
      },
    }),
    product_price_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-900' },
      options: {
        label: 'pages.blocks.suggestion-cards.product-price-color.label',
      },
    }),
    product_image_placeholder_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-50' },
      options: {
        label: 'pages.blocks.suggestion-cards.product-image-placeholder-color.label',
      },
    }),
    product_border_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-100' },
      options: {
        label: 'pages.blocks.suggestion-cards.product-border-color.label',
      },
    }),
    product_background_color: propertyValue({
      type: 'color',
      value: { type: 'hex', value: 'transparent' },
      options: {
        label: 'pages.blocks.suggestion-cards.product-background-color.label',
      },
    }),
    product_hover_colors: {
      backgroundColor: propertyValue({
        type: 'color',
        value: { type: 'palette', value: 'neutral-50' },
        options: {
          label:
            'pages.blocks.suggestion-cards.product-hover-colors.background-color.label',
          help: 'pages.blocks.suggestion-cards.product-hover-colors.help',
        },
      }),
      nameColor: propertyValue({
        type: 'color',
        value: { type: 'default', value: '' },
        options: {
          label: 'pages.blocks.suggestion-cards.product-hover-colors.name-color.label',
          colorInheritFrom: 'product_name_color',
        },
      }),
      descriptionColor: propertyValue({
        type: 'color',
        value: { type: 'default', value: '' },
        options: {
          label:
            'pages.blocks.suggestion-cards.product-hover-colors.description-color.label',
          colorInheritFrom: 'product_description_color',
        },
      }),
      priceColor: propertyValue({
        type: 'color',
        value: { type: 'default', value: '' },
        options: {
          label: 'pages.blocks.suggestion-cards.product-hover-colors.price-color.label',
          colorInheritFrom: 'product_price_color',
        },
      }),
    },
  }),
  () => ({
    tabs: [
      {
        label: 'pages.blocks.suggestion-cards.tab.content.label',
        fieldsets: [
          {
            legend: 'pages.blocks.suggestion-cards.fieldset.header.label',
            fields: [
              'title',
              'description',
              'title_color',
              'text_color',
            ],
          },
          {
            legend: 'pages.blocks.suggestion-cards.fieldset.suggestions.label',
            fields: [
              'suggestions_set',
              'display_type',
              'is_suggestion_card_sticky',
              'background_color',
            ],
          },
          {
            legend: 'pages.blocks.suggestion-cards.fieldset.product-cards.label',
            fields: [
              'product_name_color',
              'product_description_color',
              'product_price_color',
              'product_image_placeholder_color',
              'product_border_color',
              'product_background_color',
            ],
          },
          {
            legend: 'pages.blocks.suggestion-cards.fieldset.hover.label',
            fields: [
              'product_hover_colors.backgroundColor',
              'product_hover_colors.nameColor',
              'product_hover_colors.descriptionColor',
              'product_hover_colors.priceColor',
            ],
          },
          {
            legend: 'pages.blocks.suggestion-cards.fieldset.kiosk-card.label',
            fields: [
              {
                prop: 'card_color',
                visibleWhen: { display_type: 'kiosk_grid' },
              },
              {
                prop: 'button_background_color',
                visibleWhen: { display_type: 'kiosk_grid' },
              },
              {
                prop: 'button_text_color',
                visibleWhen: { display_type: 'kiosk_grid' },
              },
              {
                prop: 'secondary_color',
                visibleWhen: { display_type: 'kiosk_grid' },
              },
              {
                prop: 'price_color',
                visibleWhen: { display_type: 'kiosk_grid' },
              },
            ],
          },
        ],
      },
    ],
  })
);

export function suggestionCardsBlockDescriptor(): IBlockDescriptor;
export function suggestionCardsBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function suggestionCardsBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedSuggestionCardsBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedSuggestionCardsBlockDescriptor,
    suggestionCardsBlockProperties,
    fieldOrder,
  );
}

export {
  suggestionCardsBlockBlocDefinition,
  suggestionCardsBlockDashboardLayout,
  suggestionCardsBlockProperties,
  suggestionCardsBlockConfig,
  suggestionCardsBlockResolveConfig,
  suggestionCardsBlockDefinitionSetupScenarios,
};

export type SuggestionCardsBlockConfig = typeof suggestionCardsBlockConfig;
